import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Filter, BookOpen, Clock, ArrowRight, AlertTriangle } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { resolveImg } from "@/lib/imageUtils";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function OrgMappedCoursesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [mandOnly, setMandOnly] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (mandOnly !== "all") params.mandatory_or_optional = mandOnly;
      const { data } = await api.get("/org/courses", { params });
      setRows(data.data.courses);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [search, mandOnly]);
  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout title="Mapped courses" breadcrumbs={["My Institute", "Courses"]}>
      <div className="space-y-4" data-testid="org-mapped-courses">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search courses" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="org-course-search" />
          </div>
          <Select value={mandOnly} onValueChange={setMandOnly}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              <SelectItem value="mandatory">Mandatory only</SelectItem>
              <SelectItem value="optional">Optional only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? <LoadingBlock label="Loading courses…" /> : rows.length === 0 ? (
          <EmptyState title="No mapped courses" description="Steward Super Admin has not mapped any courses to your organization yet." icon={BookOpen} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((row) => {
              const c = row.course;
              return (
                <div key={row.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition">
                  {c.thumbnail ? (
                    <img src={resolveImg(c.thumbnail)} alt="" className="h-32 w-full object-cover" />
                  ) : (
                    <div className="h-32 w-full bg-gradient-to-br from-[#101876] to-[#329112] flex items-center justify-center">
                      <BookOpen className="h-10 w-10 text-white/70" />
                    </div>
                  )}
                  <div className="flex-1 p-4 space-y-2">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#329112]">
                      {c.category_name || "Course"}
                      {row.mandatory_or_optional === "mandatory" && (
                        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-rose-700"><AlertTriangle className="h-3 w-3" /> Mandatory</span>
                      )}
                    </div>
                    <div className="text-sm font-bold text-slate-900 line-clamp-2">{c.course_title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-3">
                      <span className="capitalize">{c.course_type?.replace(/_/g, " ")}</span>
                      <span>·</span>
                      <span className="capitalize">{c.course_level}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.estimated_duration_hours}h</span>
                    </div>
                    <div className="text-xs text-slate-500">{row.assignments_count || 0} staff assigned</div>
                  </div>
                  <Link to={`/app/hospital/courses/${c.id}`} className="border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 text-xs font-bold text-[#101876] hover:bg-slate-100 flex items-center justify-between" data-testid={`org-course-open-${c.id}`}>
                    Manage & assign <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
